import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { alpha, createTheme, darken, lighten } from '@mui/material/styles';
import { observer } from 'mobx-react';
import { useEffect, useMemo } from 'react';
import { getTheme, initRubiksLicense, LocalizationProvider, ThemeTypeProvider, useRubiksTheme } from 'ml-rubiks-styleguide';
import { injectTSDI } from 'ml-webclient-common/src/tsdi';
import { Config } from '../../core/config';
import { clientParamToThemeType } from '../../core/search-params';
import { STATE_FOCUS_ALPHA, STATE_FOCUS_VISIBLE_ALPHA, STATE_HOVER_ALPHA, STATE_OUTLINED_BORDER_ALPHA, STATE_SELECTED_ALPHA, STEPPER_SECONDARY_ALPHA, TEXT_DISABLED_ALPHA, TEXT_SECONDARY_ALPHA, TONAL_OFFSET_DARK, TONAL_OFFSET_LIGHT } from '../../core/styling-constants';
import { FastFinanceLinkStore } from '../../store';
function convertStylingToThemeOverrides(styling) {
    if (!styling) {
        return {};
    }
    const stepperBgColor = styling.backgroundColor2;
    const stepperTextColor = styling.textColor;
    return {
        palette: {
            ...(styling.highlightColor && {
                primary: {
                    main: styling.highlightColor,
                    dark: darken(styling.highlightColor, TONAL_OFFSET_DARK),
                    light: lighten(styling.highlightColor, TONAL_OFFSET_LIGHT),
                    ...(styling.highlightTextColor && {
                        contrastText: styling.highlightTextColor
                    }),
                    _states: {
                        hover: alpha(styling.highlightColor, STATE_HOVER_ALPHA),
                        selected: alpha(styling.highlightColor, STATE_SELECTED_ALPHA),
                        focus: alpha(styling.highlightColor, STATE_FOCUS_ALPHA),
                        'focus-visible': alpha(styling.highlightColor, STATE_FOCUS_VISIBLE_ALPHA),
                        'outlined-border': alpha(styling.highlightColor, STATE_OUTLINED_BORDER_ALPHA)
                    }
                },
                action: {
                    hover: alpha(styling.highlightColor, STATE_HOVER_ALPHA),
                    selected: alpha(styling.highlightColor, STATE_SELECTED_ALPHA),
                    focus: alpha(styling.highlightColor, STATE_FOCUS_ALPHA)
                }
            }),
            ...(styling.textColor && {
                text: {
                    primary: styling.textColor,
                    secondary: alpha(styling.textColor, TEXT_SECONDARY_ALPHA),
                    disabled: alpha(styling.textColor, TEXT_DISABLED_ALPHA)
                }
            }),
            ...(styling.errorColor && {
                error: { main: styling.errorColor }
            }),
            ...(styling.hrColor && { divider: styling.hrColor }),
            ...(stepperTextColor && {
                components: {
                    stepper: {
                        background: stepperBgColor,
                        done: stepperTextColor,
                        default: alpha(stepperTextColor, STEPPER_SECONDARY_ALPHA),
                        connector: alpha(stepperTextColor, STEPPER_SECONDARY_ALPHA),
                        contrast: stepperTextColor,
                        swipe: stepperTextColor
                    }
                }
            }),
            ...(!stepperTextColor &&
                stepperBgColor && {
                components: {
                    stepper: {
                        background: stepperBgColor
                    }
                }
            }),
            ...((styling.backgroundColor || styling.backgroundColor2) && {
                background: {
                    ...(styling.backgroundColor && {
                        default: styling.backgroundColor
                    }),
                    ...(styling.backgroundColor2 && {
                        paper: styling.backgroundColor2
                    })
                }
            })
        },
        ...(stepperTextColor && {
            components: {
                MuiStepLabel: {
                    styleOverrides: {
                        label: {
                            color: stepperTextColor,
                            '&.Mui-completed': {
                                color: alpha(stepperTextColor, TEXT_DISABLED_ALPHA)
                            },
                            '&.Mui-active': {
                                color: stepperTextColor
                            }
                        }
                    }
                }
            }
        })
    };
}
export const Bootstrap = observer(function Bootstrap({ children }) {
    const config = injectTSDI(Config);
    const store = injectTSDI(FastFinanceLinkStore);
    const themeType = store.client
        ? clientParamToThemeType(store.client)
        : 'magicline';
    const { ThemeProvider, FontProvider } = useRubiksTheme({ type: themeType });
    const baseTheme = useMemo(() => getTheme(themeType), [themeType]);
    const theme = useMemo(() => {
        const overrides = convertStylingToThemeOverrides(store.styling);
        return createTheme(baseTheme, overrides);
    }, [baseTheme, store.styling]);
    useEffect(() => {
        initRubiksLicense();
    }, []);
    const locale = config.getLocale();
    const localeConfig = useMemo(() => ({
        timezone: 'Europe/Berlin',
        locale
    }), [locale]);
    return (_jsx(ThemeTypeProvider, { value: { themeType }, children: _jsx(ThemeProvider, { theme: theme, children: _jsxs(LocalizationProvider, { timezone: localeConfig.timezone, adapterLocale: localeConfig.locale, children: [_jsx(FontProvider, { theme: themeType }), children] }) }) }));
});
